import "@/App.css";
import "@/styles/trattoria.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SuperAuthProvider } from "./contexts/SuperAuthContext";
import { ClinicBrandingProvider } from "./context/ClinicBrandingContext";
import { Header, Footer } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import SuperProtectedRoute from "./components/SuperProtectedRoute";
import { PizzeriaHeader, PizzeriaFooter } from "./components/pizzeria/PizzeriaChrome";
import PageCurtain from "./components/pizzeria/PageCurtain";
import { useClinicBranding, clinicLogo } from "./context/ClinicBrandingContext";
import PizzeriaHome from "./pages/pizzeria/PizzeriaHome";
import LegalPage from "./pages/LegalPage";
import BajaPage from "./pages/BajaPage";

// Public Pages
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import BookingPage from "./pages/BookingPage";
import ReviewsPage from "./pages/ReviewsPage";
import ContactPage from "./pages/ContactPage";
import MenuOrderPage from "./pages/MenuOrderPage";
import CheckoutPage from "./pages/CheckoutPage";
import PayOrderPage from "./pages/PayOrderPage";
import AdminOrders from "./pages/AdminOrders";
import AdminDeliveryDrivers from "./pages/AdminDeliveryDrivers";
import AdminPizasStore from "./pages/AdminPizasStore";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import AdminMenu from "./pages/AdminMenu";
import AdminKitchen from "./pages/AdminKitchen";
import AdminInventory from "./pages/AdminInventory";

// Admin Pages
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminLayout from "./pages/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProducts from "./pages/AdminProducts";
import AdminAppointments from "./pages/AdminAppointments";
import AdminPendingAppointments from "./pages/AdminPendingAppointments";
import AdminReviews from "./pages/AdminReviews";
import AdminSchedule from "./pages/AdminSchedule";
import AdminWhatsApp from "./pages/AdminWhatsApp";
import AdminSettings from "./pages/AdminSettings";
import AdminSitePublic from "./pages/AdminSitePublic";
import AdminManageAdmins from "./pages/AdminManageAdmins";
import AdminSubscriptionReturn from "./pages/AdminSubscriptionReturn";
import AdminComanda from "./pages/AdminComanda";
import AdminCash from "./pages/AdminCash";

// Super SaaS
import SuperLogin from "./pages/super/SuperLogin";
import SuperLayout from "./pages/super/SuperLayout";
import SuperClient from "./pages/super/SuperClient";
import SuperBilling from "./pages/super/SuperBilling";

const PIZAS_MODE = process.env.REACT_APP_PIZAS_MODE === "1";

// Sitio público de la pizzería: estilo «Trattoria cálida artesanal» (clase .trattoria)
function PizzeriaPublicLayout({ children }) {
  const { info } = useClinicBranding();
  useEffect(() => {
    // Cada ruta monta su propio layout: empezar arriba al cambiar de página
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="trattoria min-h-screen flex flex-col">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:rounded-full focus:shadow"
      >
        Saltar al contenido
      </a>
      <PageCurtain logo={clinicLogo(info)} />
      <PizzeriaHeader />
      <main id="contenido" className="t-page-in flex-1">{children}</main>
      <PizzeriaFooter />
    </div>
  );
}

// Public Layout Component
function PublicLayout({ children }) {
  if (PIZAS_MODE) return <PizzeriaPublicLayout>{children}</PizzeriaPublicLayout>;
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ClinicBrandingProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout>{PIZAS_MODE ? <PizzeriaHome /> : <HomePage />}</PublicLayout>} />
            <Route path="/productos" element={<PublicLayout><ProductsPage /></PublicLayout>} />
            {/* HOTFIX: /agendar (citas de podologia) no aplica al sitio de pizzas. */}
            <Route path="/agendar" element={process.env.REACT_APP_PIZAS_MODE === '1' ? <Navigate to="/pedir" replace /> : <PublicLayout><BookingPage /></PublicLayout>} />
            <Route path="/resenas" element={<PublicLayout><ReviewsPage /></PublicLayout>} />
            <Route path="/contacto" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/pedir" element={<PublicLayout><MenuOrderPage /></PublicLayout>} />
            <Route path="/menu" element={<PublicLayout><MenuOrderPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
            {/* Paquete legal v2026-10-09: páginas legales y baja de promociones (enlaces relativos) */}
            <Route path="/aviso-de-privacidad" element={<PublicLayout><LegalPage docKey="privacy" /></PublicLayout>} />
            <Route path="/terminos" element={<PublicLayout><LegalPage docKey="terms" /></PublicLayout>} />
            <Route path="/cookies" element={<PublicLayout><LegalPage docKey="cookies" /></PublicLayout>} />
            <Route path="/baja" element={<PublicLayout><BajaPage /></PublicLayout>} />
            <Route path="/pagar/checkout/:checkoutId" element={<PayOrderPage />} />
            <Route path="/pagar/:orderId" element={<PayOrderPage />} />
            
            {/* Admin Login */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/suscripcion/retorno" element={<AdminSubscriptionReturn />} />

            {/* /admin sin sesión → login (evita pantalla en blanco si el layout falla al cargar) */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="productos" element={<AdminProducts />} />
              <Route path="citas/por-confirmar" element={<AdminPendingAppointments />} />
              <Route path="citas" element={<AdminAppointments />} />
              <Route path="resenas" element={<AdminReviews />} />
              <Route path="horarios" element={<AdminSchedule />} />
              <Route path="whatsapp" element={<AdminWhatsApp />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="sitio-publico" element={<AdminSitePublic />} />
              <Route path="administradores" element={<AdminManageAdmins />} />
              <Route path="pedidos" element={<AdminOrders />} />
              <Route path="comanda" element={<AdminComanda />} />
              <Route path="caja" element={<AdminCash />} />
              <Route path="cocina" element={<AdminKitchen />} />
              <Route path="repartidores" element={<AdminDeliveryDrivers />} />
              <Route path="menu" element={<AdminMenu />} />
              <Route path="almacen" element={<AdminInventory />} />
              <Route path="config-tienda" element={<AdminPizasStore />} />
              <Route path="cupones" element={<AdminPromoCodes />} />
            </Route>

            {/* Super SaaS (rol superadmin, distinto de super_admin de tienda) */}
            <Route
              path="/super/login"
              element={
                <SuperAuthProvider>
                  <SuperLogin />
                </SuperAuthProvider>
              }
            />
            <Route
              path="/super"
              element={
                <SuperAuthProvider>
                  <SuperProtectedRoute>
                    <SuperLayout />
                  </SuperProtectedRoute>
                </SuperAuthProvider>
              }
            >
              <Route index element={<SuperClient />} />
              <Route path="suscripcion" element={<SuperBilling />} />
            </Route>
          </Routes>
          </ClinicBrandingProvider>
        </BrowserRouter>
        {/* Abajo al centro, por encima de la barra del carrito: no tapa el encabezado ni su botón */}
        <Toaster
          richColors
          position="bottom-center"
          offset={{ bottom: 128 }}
          mobileOffset={{ bottom: 210 }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
